import axios from 'axios'
import { getMessageMapFromCommonResponse, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import {
  commonHeadersMPWeb,
  supportApiURL,
} from 'src/supportPortal/services/services'
import { getCountryCode, getMainLanguageCode, platformWeb } from 'src/consumer/services'

const VC_AUDIT_SESSIONS_API = '/api/secure/cc/support/vc-audit/sessions'

function buildHeaders() {
  const token = localStorage.getItem('token')
  const countryCode = getCountryCode()
  const mainLanguageCode = getMainLanguageCode()

  return {
    token,
    countryCode,
    mainLanguageCode,
    headers: {
      Authorization: `Bearer ${token}`,
      ...commonHeadersMPWeb,
      'ef-user-country-code': countryCode,
      'ef-app-language': mainLanguageCode,
    },
  }
}

function buildErrorMessage(error, fallbackMessage) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallbackMessage
  )
}

function buildPageFallback(page = 0, size = 20) {
  return {
    content: [],
    totalElements: 0,
    totalPages: 0,
    size,
    number: page,
  }
}

function normalizeArrayParam(value) {
  if (!value) {
    return undefined
  }
  if (Array.isArray(value)) {
    return value.filter(Boolean)
  }
  return [value].filter(Boolean)
}

export async function listVcAuditSessions({
  page = 0,
  size = 20,
  clinicBrandId,
  clinicLocationId,
  eventType,
  sessionStatusIds,
  startTime,
  endTime,
} = {}) {
  const { token, countryCode, mainLanguageCode, headers } = buildHeaders()

  if (!token) {
    return {
      ok: false,
      message: 'Missing auth token.',
      sessionsPage: buildPageFallback(page, size),
      liveCount: 0,
    }
  }

  try {
    const response = await axios.get(`${supportApiURL}${VC_AUDIT_SESSIONS_API}`, {
      headers,
      params: {
        languageCode: mainLanguageCode,
        platform: platformWeb,
        countryCode,
        page,
        size,
        clinicBrandId: clinicBrandId || undefined,
        clinicLocationId: clinicLocationId || undefined,
        eventTypes: normalizeArrayParam(eventType),
        sessionStatusIds: normalizeArrayParam(sessionStatusIds),
        startTime: startTime || undefined,
        endTime: endTime || undefined,
      },
      paramsSerializer: {
        indexes: null,
      },
    })

    if (!isCommonResponseSuccessfulV2(response)) {
      return {
        ok: false,
        message: getMessageMapFromCommonResponse(response) || 'Unable to load VC audit sessions.',
        sessionsPage: buildPageFallback(page, size),
        liveCount: 0,
      }
    }

    const data = response?.data?.data || {}
    return {
      ok: true,
      sessionsPage: data.sessions || buildPageFallback(page, size),
      liveCount: data.liveCount || 0,
      rawResponse: response,
    }
  } catch (error) {
    console.error('Error loading VC audit sessions:', error)
    return {
      ok: false,
      message: buildErrorMessage(error, 'Unable to load VC audit sessions.'),
      sessionsPage: buildPageFallback(page, size),
      liveCount: 0,
    }
  }
}

export async function getVcAuditSessionDetails(bookedEventId) {
  const { token, countryCode, mainLanguageCode, headers } = buildHeaders()

  if (!token || !bookedEventId) {
    return {
      ok: false,
      message: 'Booked event id is required.',
      session: null,
    }
  }

  try {
    const response = await axios.get(`${supportApiURL}${VC_AUDIT_SESSIONS_API}/${bookedEventId}`, {
      headers,
      params: {
        languageCode: mainLanguageCode,
        platform: platformWeb,
        countryCode,
      },
    })

    if (!isCommonResponseSuccessfulV2(response)) {
      return {
        ok: false,
        message: getMessageMapFromCommonResponse(response) || 'Unable to load session details.',
        session: null,
      }
    }

    return {
      ok: true,
      session: response?.data?.data?.session || null,
      rawResponse: response,
    }
  } catch (error) {
    console.error('Error loading VC audit session details:', error)
    return {
      ok: false,
      message: buildErrorMessage(error, 'Unable to load session details.'),
      session: null,
    }
  }
}

export async function getVcAuditViewerBootstrap(bookedEventId) {
  const { token, countryCode, mainLanguageCode, headers } = buildHeaders()

  if (!token || !bookedEventId) {
    return {
      ok: false,
      message: 'Booked event id is required.',
      bootstrap: null,
    }
  }

  try {
    const response = await axios.get(
      `${supportApiURL}${VC_AUDIT_SESSIONS_API}/${bookedEventId}/viewer-bootstrap`,
      {
        headers,
        params: {
          languageCode: mainLanguageCode,
          platform: platformWeb,
          countryCode,
        },
      },
    )

    if (!isCommonResponseSuccessfulV2(response)) {
      return {
        ok: false,
        message:
          getMessageMapFromCommonResponse(response) ||
          'Unable to load the live viewer.',
        bootstrap: null,
      }
    }

    return {
      ok: true,
      bootstrap: response?.data?.data || null,
      rawResponse: response,
    }
  } catch (error) {
    console.error('Error loading VC audit viewer bootstrap:', error)
    return {
      ok: false,
      message: buildErrorMessage(error, 'Unable to load the live viewer.'),
      bootstrap: null,
    }
  }
}

export async function listVcAuditRecordings(bookedEventId) {
  const { token, countryCode, mainLanguageCode, headers } = buildHeaders()

  if (!token || !bookedEventId) {
    return {
      ok: false,
      message: 'Booked event id is required.',
      session: null,
      recordings: [],
    }
  }

  try {
    const response = await axios.get(
      `${supportApiURL}${VC_AUDIT_SESSIONS_API}/${bookedEventId}/recordings`,
      {
        headers,
        params: {
          languageCode: mainLanguageCode,
          platform: platformWeb,
          countryCode,
        },
      },
    )

    if (!isCommonResponseSuccessfulV2(response)) {
      return {
        ok: false,
        message:
          getMessageMapFromCommonResponse(response) ||
          'Unable to load session recordings.',
        session: null,
        recordings: [],
      }
    }

    const data = response?.data?.data || {}
    return {
      ok: true,
      session: data.session || null,
      recordings: Array.isArray(data.recordings) ? data.recordings : [],
      rawResponse: response,
    }
  } catch (error) {
    console.error('Error loading VC audit recordings:', error)
    return {
      ok: false,
      message: buildErrorMessage(error, 'Unable to load session recordings.'),
      session: null,
      recordings: [],
    }
  }
}

export async function getVcAuditRecordingPlayback(bookedEventId, recordingId) {
  const { token, countryCode, mainLanguageCode, headers } = buildHeaders()

  if (!token || !bookedEventId || !recordingId) {
    return {
      ok: false,
      message: 'Booked event id and recording id are required.',
      session: null,
      recording: null,
      playback: null,
    }
  }

  try {
    const response = await axios.get(
      `${supportApiURL}${VC_AUDIT_SESSIONS_API}/${bookedEventId}/recordings/${recordingId}/playback`,
      {
        headers,
        params: {
          languageCode: mainLanguageCode,
          platform: platformWeb,
          countryCode,
        },
      },
    )

    if (!isCommonResponseSuccessfulV2(response)) {
      return {
        ok: false,
        message:
          getMessageMapFromCommonResponse(response) ||
          'Unable to load recording playback.',
        session: null,
        recording: null,
        playback: null,
      }
    }

    const data = response?.data?.data || {}
    return {
      ok: true,
      session: data.session || null,
      recording: data.recording || null,
      playback: data.playback || null,
      rawResponse: response,
    }
  } catch (error) {
    console.error('Error loading VC audit recording playback:', error)
    return {
      ok: false,
      message: buildErrorMessage(error, 'Unable to load recording playback.'),
      session: null,
      recording: null,
      playback: null,
    }
  }
}
